import { type SVGProps, useId } from "react"

import {
  BatyrIcon,
  BatyrLogoHorizontal,
  BatyrLogoStacked,
} from "@/assets/images/brand"

export enum BatyrLogoVariant {
  HORIZONTAL = "horizontal",
  STACKED = "stacked",
  ICON = "icon",
}

const LOGO_SOURCES = {
  [BatyrLogoVariant.HORIZONTAL]: BatyrLogoHorizontal,
  [BatyrLogoVariant.STACKED]: BatyrLogoStacked,
  [BatyrLogoVariant.ICON]: BatyrIcon,
} as const

const LOGO_DIMENSIONS = {
  [BatyrLogoVariant.HORIZONTAL]: {
    viewBox: "0 0 196 96",
    width: 196,
    height: 96,
  },
  [BatyrLogoVariant.STACKED]: {
    viewBox: "0 0 109 114",
    width: 109,
    height: 114,
  },
  [BatyrLogoVariant.ICON]: {
    viewBox: "0 0 138 115",
    width: 138,
    height: 115,
  },
} as const

interface BatyrLogoProps extends Omit<SVGProps<SVGSVGElement>, "children"> {
  variant?: BatyrLogoVariant
  color?: string
}

/**
 * Batyr Logo component
 * @param width - SVG width (defaults to the variant's intrinsic width)
 * @param height - SVG height (defaults to the variant's intrinsic height)
 * @param color - When set, tints the logo to this fill color via an SVG mask
 * @param variant - The variant of the logo to render
 * @param className - Tailwind or other classes (e.g. `h-8 w-auto` for responsive sizing)
 * @style - CSS properties to apply to the SVG
 *
 * @returns - A React component that renders the Batyr logo
 *
 * @example
 * <BatyrLogo className="size-full" variant={BatyrLogoVariant.STACKED} />
 * <BatyrLogo className="size-full" variant={BatyrLogoVariant.HORIZONTAL} />
 * <BatyrLogo className="size-full" variant={BatyrLogoVariant.ICON} />
 *
 */
export function BatyrLogo({
  className,
  width,
  height,
  color,
  variant = BatyrLogoVariant.ICON,
  style,
  ...props
}: BatyrLogoProps) {
  const maskId = useId()
  const dimensions = LOGO_DIMENSIONS[variant]
  const src = LOGO_SOURCES[variant]

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={dimensions.viewBox}
      width={width ?? dimensions.width}
      height={height ?? dimensions.height}
      aria-hidden
      className={className}
      style={style}
      {...props}
    >
      {color ? (
        <>
          <defs>
            <mask id={maskId} style={{ maskType: "alpha" }}>
              <image
                href={src}
                width="100%"
                height="100%"
                preserveAspectRatio="xMidYMid meet"
              />
            </mask>
          </defs>
          <rect
            width="100%"
            height="100%"
            fill={color}
            mask={`url(#${maskId})`}
          />
        </>
      ) : (
        <image
          href={src}
          width="100%"
          height="100%"
          preserveAspectRatio="xMidYMid meet"
        />
      )}
    </svg>
  )
}
